import React, { useRef, useState } from "react";
import classNames from "classnames";
import { hasClass } from "../common/Dom";
import { IndexListProps, IndexListState } from "./PropsType";

export default function IndexList(props: IndexListProps) {
  const letters: string = "ABCDEFGHIJKLMNOPQRSUVWXYZ";
  const navNode = useRef(null);
  const isMobile: boolean = "ontouchstart" in document;
  const dragging = useRef(false);
  const touches: any = {
    startY: 0,
  };
  const scrollNode = useRef(null);
  const { children, className, prefixCls = "bm-IndexList" } = props;
  const styleClass = classNames(prefixCls, className);
  const [scrollTop, setScrollTop] = useState(0);
  const [current, setCurrent] = useState<string>('');

  const splitLetter = () => {
    return letters.split("");
  };

  const getPoint = (e: any) => {
    const touchEvent = isMobile ? e.changedTouches[0] : e;
    return {
      x: touchEvent.pageX || touchEvent.clientX,
      y: touchEvent.pageY || touchEvent.clientY,
    };
  };

  const getOffsetTop = (element: any, condition: (event: any) => void) => {
    let actualTop = element.offsetTop;
    let current = element.offsetParent;
    while (current !== null && condition(current)) {
      actualTop += current.offsetTop;
      current = current.offsetParent;
    }
    return actualTop;
  };

  const scrollTo = (event: any) => {
    let index = "";
    if (hasClass(event.target, `${prefixCls}-nav-item`) && scrollNode.current) {
      index = event.target.getAttribute("data-index");
      setCurrent(index);
      const target = (scrollNode.current as HTMLDivElement).querySelector(
        `[data-index=${index}]`
      );
      let top = 0;
      if (target) {
        top = getOffsetTop(target, (parent: any) => {
          return !hasClass(parent, `${prefixCls}-content`);
        });
        (scrollNode.current as HTMLDivElement).scrollTop = top;
      }
    }
  };

  const swipeStart = (event: any) => {
    if (scrollNode.current) {
      const scrollHeight =
        (scrollNode.current as HTMLDivElement).scrollHeight -
        (scrollNode.current as HTMLDivElement).offsetHeight;
      if (scrollHeight > 0) {
        const { y } = getPoint(event);
        dragging.current = true;
        scrollTo(event);
        touches.startY = y;
        document.addEventListener(
          isMobile ? "touchmove" : "mousemove",
          swipeMove
        );
        document.addEventListener(isMobile ? "touchend" : "mouseup", swipeEnd);
      }
    }
  };

  const swipeMove = (event: any) => {
    event.stopPropagation();
    if (dragging.current) {
      scrollTo(event);
      const { y } = getPoint(event);
      const diffY = y - touches.startY + scrollTop;
      setScrollTop(Math.max(Math.min(0, diffY), -touches.moveHeight));
      touches.startY = y;
    }
  };

  const swipeEnd = (event: any) => {
    event.stopPropagation();
    dragging.current = false;
    setCurrent('');
    document.removeEventListener(
      isMobile ? "touchmove" : "mousemove",
      swipeMove
    );
    document.removeEventListener(isMobile ? "touchend" : "mouseup", swipeEnd);
  };

  return (
    <div className={styleClass}>
      <div className={`${prefixCls}-content`} ref={scrollNode}>
        <ul>{children}</ul>
      </div>
      <div
        className={`${prefixCls}-nav`}
        ref={navNode}
        onMouseDown={isMobile ? () => false : swipeStart}
        onTouchStart={isMobile ? swipeStart : () => false}
      >
        <ul>
          {splitLetter().map((letter: string, index: number) => {
            return (
              <li
                className={`${prefixCls}-nav-item`}
                data-index={letter}
                key={index}
              >
                {letter}
              </li>
            );
          })}
        </ul>
      </div>
      {dragging.current && <span className={`${prefixCls}-current`}>{current}</span>}
    </div>
  );
}
